'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import Carousel from './Carousel';
import AuthModal from './AuthModal';
import SiteHeader from './SiteHeader';
import '../animations.css';

export default function LandingPageContent({ carouselMedia }: { carouselMedia: string[] }) {
  const [mounted, setMounted] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const [verifiedMessage, setVerifiedMessage] = useState(false);
  const [expiredMessage, setExpiredMessage] = useState(false);
  // Where to send the visitor once they finish authenticating (e.g. a shared
  // community post they opened logged-out and clicked "log in to interact").
  const [postAuthRedirect, setPostAuthRedirect] = useState<string | null>(null);
  const { user } = useAuth();
  const router = useRouter();

  useEffect(() => {
    setMounted(true);

    const params = new URLSearchParams(window.location.search);

    // Only same-origin relative paths are honored, to avoid an open redirect.
    const rawRedirect = params.get('redirect');
    if (rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')) {
      setPostAuthRedirect(rawRedirect);
    }

    // Just verified their email → prompt login
    if (params.get('verified') === 'true') {
      setVerifiedMessage(true);
      setAuthMode('login');
      setShowAuthModal(true);
      window.history.replaceState({}, '', '/');
      return;
    }

    // Bounced here by a 401 / session expiry, or a referral signup link (/r/[code] → ?signup=1).
    const auth = params.get('auth');
    const wantSignup = auth === 'signup' || params.get('signup') === '1';
    if (auth === 'login' || wantSignup) {
      if (params.get('reason') === 'expired') setExpiredMessage(true);
      setAuthMode(wantSignup ? 'signup' : 'login');
      setShowAuthModal(true);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Once authenticated with a pending redirect target, return the visitor there
  // (e.g. back to the shared post they came from) instead of stranding them here.
  useEffect(() => {
    if (user && postAuthRedirect) {
      router.replace(postAuthRedirect);
    }
  }, [user, postAuthRedirect, router]);

  const handleUploadClick = () => {
    if (user) {
      router.push('/dashboard');
    } else {
      setAuthMode('signup');
      setShowAuthModal(true);
    }
  };

  const handleAuthClick = () => {
    if (user) {
      router.push('/dashboard');
    } else {
      setAuthMode('login');
      setShowAuthModal(true);
    }
  };

  return (
    <>

      <SiteHeader
        onLoginClick={handleAuthClick}
        className={mounted ? 'opacity-0 animate-fadeInDown' : 'opacity-0'}
      />

      <div className="flex min-h-screen items-center justify-center px-4 sm:px-6 pt-16 pb-20">
        <main className="flex flex-col items-center gap-4 sm:gap-6 md:gap-8 w-full max-w-4xl">
          <div className={`text-center space-y-3 sm:space-y-4 max-w-3xl px-2 ${mounted ? 'opacity-0 animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.2s' }}>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight leading-tight px-4">
              {/* Brand kept in the accessible/crawlable H1 name (visually hidden so
                  the minimal hero design is untouched) — the homepage must contain
                  the "Ultragoon" brand term it ranks for. */}
              <span className="sr-only">Ultragoon — </span>the ultimate nsfw AI generator.
            </h1>
            {/* Crawlable one-line description of the product for the brand query. */}
            <p className="sr-only">
              Ultragoon is an uncensored AI generator for creating and editing images and video,
              with an AI creative assistant and a creator community. Sign up free — no credit card
              required.
            </p>
            {/* data-nosnippet: promo chrome (credits pill, CTA, feature chips) is UI, not a
                description — keep it out of Google's snippet so it uses the meta description
                or the clean sentence above instead of "…30 free credits… start gooning". */}
            <div data-nosnippet className="flex flex-col items-center gap-2 sm:gap-2.5">
              <div className="inline-flex items-center gap-2 rounded-full credit-pill px-4 py-1.5">
                <span className="text-sm sm:text-base text-white/70 font-normal">sign up for</span>
                <span className="text-white font-bold text-base sm:text-lg">30 free credits</span>
              </div>
              <p className="text-xs sm:text-sm text-white/35 font-light">
                no credit card required
              </p>
            </div>
          </div>

          <div data-nosnippet className={`flex flex-col items-center gap-4 sm:gap-5 mt-1 sm:mt-2 ${mounted ? 'opacity-0 animate-fadeInUp' : 'opacity-0'}`} style={{ animationDelay: '0.4s' }}>
            <button
              onClick={handleUploadClick}
              className="px-7 sm:px-9 py-3 sm:py-3.5 btn-xray rounded-xl font-semibold text-sm sm:text-base active:scale-95 sm:hover:scale-[1.03] transition-transform duration-200 ease-out flex items-center gap-2.5 cursor-pointer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 sm:h-5 sm:w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                />
              </svg>
              start gooning
            </button>
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 px-4">
              {['uncensored', 'images + video', 'instant HD', 'no card required'].map((f) => (
                <span
                  key={f}
                  className="feature-chip rounded-full px-3 py-1 text-[11px] sm:text-xs text-white/60 whitespace-nowrap"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>

          {/* Image Carousel */}
          {carouselMedia.length > 0 && <Carousel mediaFiles={carouselMedia} />}
        </main>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => {
          setShowAuthModal(false);
          setVerifiedMessage(false);
          setExpiredMessage(false);
        }}
        defaultMode={authMode}
        showVerifiedMessage={verifiedMessage}
        showExpiredMessage={expiredMessage}
      />

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 z-40">
        <div data-nosnippet className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex flex-wrap items-center justify-center gap-2 sm:gap-6 text-[10px] sm:text-xs text-white/40">
          <a href="/terms" className="hover:text-white/70 transition-colors whitespace-nowrap">Terms of Service</a>
          <span className="text-white/20 hidden sm:inline">•</span>
          <a href="/fair-use" className="hover:text-white/70 transition-colors whitespace-nowrap">Fair Use Policy</a>
          <span className="text-white/20 hidden sm:inline">•</span>
          <a href={`mailto:${process.env.NEXT_PUBLIC_ABUSE_EMAIL || 'abuse@ultragoon.co'}`} className="hover:text-white/70 transition-colors whitespace-nowrap">Report Abuse</a>
        </div>
      </footer>
    </>
  );
}

